import { AuthResultDto, ICurrentUserDto } from '../web-api-client';

/**
 * Phiên đăng nhập của người dùng, lưu trong localStorage để đóng trình duyệt xong mở lại vẫn còn.
 *
 * Access token sống rất ngắn; refresh token mới là thứ giữ phiên, và nó xoay vòng — mỗi lần làm mới
 * sẽ nhận một token khác và token cũ bị thu hồi. Vì vậy chỗ lưu này phải là nguồn duy nhất, không
 * được để hai bản sao token ở hai nơi rồi gửi nhầm bản cũ: máy chủ coi việc dùng lại token đã thu
 * hồi là dấu hiệu bị đánh cắp và sẽ hủy toàn bộ phiên của tài khoản đó.
 */
export interface Session {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: string;
  refreshTokenExpiresAt: string;
  user: ICurrentUserDto;
}

const STORAGE_KEY = 'todoapp.session';

type Listener = (session: Session | null) => void;

const listeners = new Set<Listener>();

let current: Session | null = read();

function read(): Session | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    // localStorage có thể bị chặn (chế độ riêng tư, chính sách trình duyệt). Khi đó ứng dụng vẫn
    // chạy được, chỉ là mỗi lần mở lại phải đăng nhập.
    return null;
  }
}

export function load(): Session | null {
  return current;
}

export function save(session: Session): void {
  current = session;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* không lưu được thì phiên chỉ sống trong tab hiện tại */
  }

  listeners.forEach(listener => listener(session));
}

export function clear(): void {
  current = null;

  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* bỏ qua */
  }

  listeners.forEach(listener => listener(null));
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function fromAuthResult(result: AuthResultDto): Session {
  return {
    accessToken: result.accessToken ?? '',
    refreshToken: result.refreshToken ?? '',
    accessTokenExpiresAt: result.accessTokenExpiresAt ?? '',
    refreshTokenExpiresAt: result.refreshTokenExpiresAt ?? '',
    user: result.user ?? {}
  };
}

/** Cập nhật hồ sơ người dùng mà không đụng tới token. */
export function updateUser(user: ICurrentUserDto): void {
  if (current) {
    save({ ...current, user });
  }
}

// Đăng xuất ở một tab thì các tab khác cũng phải rời khỏi màn hình dữ liệu, chứ không phải chờ đến
// lần gọi API kế tiếp mới phát hiện ra mình đã mất phiên.
window.addEventListener('storage', event => {
  if (event.key !== STORAGE_KEY) return;

  current = read();
  listeners.forEach(listener => listener(current));
});
